import { useEffect, useMemo, useState } from "react";
import { App, Form } from "antd";
import dayjs from "dayjs";
import MasterContentService from "services/master/content";

const service = new MasterContentService();

const defaultSEO = {
  meta_title: "",
  meta_description: "",
  meta_keywords: [],
  canonical_url: "",
  og_title: "",
  og_description: "",
  og_image: null,
  robots: "index,follow",
};

const defaultPost = {
  id: null,
  title: "",
  slug: "",
  excerpt: "",
  status: "draft",
  publish_at: null,
  unpublish_at: null,
  content_html: "",
  facebook_post_content: "",
  content_blocks: [],
  featured_image: null,
  facebook_image: null,
  gallery: [],
  category_ids: [],
  tag_ids: [],
  seo: defaultSEO,
};

function getPublicWebsiteBaseURL() {
  if (typeof window === "undefined") return "";

  const { protocol, hostname } = window.location;

  if (["localhost", "127.0.0.1"].includes(hostname)) {
    return "http://localhost:9000";
  }

  const publicHost = hostname
    .replace(/^admin\./i, "")
    .replace(/^master001\./i, "");

  return `${protocol}//${publicHost}`;
}

function getPublicSitemapURL() {
  const baseURL = getPublicWebsiteBaseURL();
  if (!baseURL) {
    return "";
  }
  return `${baseURL.replace(/\/$/, "")}/content-sitemap.xml`;
}

function getPrimarySitemapURL() {
  const baseURL = getPublicWebsiteBaseURL();
  if (!baseURL) {
    return "";
  }
  return `${baseURL.replace(/\/$/, "")}/sitemap.xml`;
}

function toDatePickerValue(value) {
  if (!value) return null;
  const d = dayjs(value);
  return d.isValid() ? d : null;
}

function toISO(value) {
  if (!value) return null;
  const d = dayjs(value);
  if (!d.isValid()) return null;
  return d.toISOString();
}

const useComponent = () => {
  const { message, modal } = App.useApp();
  const [postForm] = Form.useForm();
  const [brandForm] = Form.useForm();

  const [loading, setLoading] = useState(false);
  const [posts, setPosts] = useState([]);
  const [totalPosts, setTotalPosts] = useState(0);
  const [postPaging, setPostPaging] = useState({ page: 1, page_size: 20 });
  const [postFilters, setPostFilters] = useState({ info: "", status: undefined });
  const [selectedPostIDs, setSelectedPostIDs] = useState([]);
  const [bulkPostStatus, setBulkPostStatus] = useState("draft");
  const [bulkUpdatingStatus, setBulkUpdatingStatus] = useState(false);

  const [categories, setCategories] = useState([]);
  const [tags, setTags] = useState([]);

  const [editorOpen, setEditorOpen] = useState(false);
  const [editingPost, setEditingPost] = useState(defaultPost);
  const [editorLoading, setEditorLoading] = useState(false);
  const [revisionLoading, setRevisionLoading] = useState(false);
  const [revisions, setRevisions] = useState([]);
  const [aiGenerating, setAIGenerating] = useState(false);
  const [aiFacebookGenerating, setAIFacebookGenerating] = useState(false);
  const [aiFacebookContent, setAIFacebookContent] = useState("");
  const [aiImageGenerating, setAIImageGenerating] = useState(false);
  const [aiImagePrompt, setAIImagePrompt] = useState("");
  const [aiGeneratedImage, setAIGeneratedImage] = useState(null);
  const [bulkAIOpen, setBulkAIOpen] = useState(false);
  const [bulkAITopic, setBulkAITopic] = useState("");
  const [bulkAICount, setBulkAICount] = useState(5);
  const [bulkAITopics, setBulkAITopics] = useState([]);
  const [bulkAISelectedIDs, setBulkAISelectedIDs] = useState([]);
  const [bulkAIGeneratingTopics, setBulkAIGeneratingTopics] = useState(false);
  const [bulkAICreatingPosts, setBulkAICreatingPosts] = useState(false);
  const [pingingGoogleSitemap, setPingingGoogleSitemap] = useState(false);

  const [savingCategory, setSavingCategory] = useState(false);
  const [savingTag, setSavingTag] = useState(false);
  const [categoryName, setCategoryName] = useState("");
  const [tagName, setTagName] = useState("");
  const [brandLoading, setBrandLoading] = useState(false);
  const [brandSaving, setBrandSaving] = useState(false);

  const loadPosts = async (override = {}) => {
    setLoading(true);
    try {
      const payload = {
        ...postFilters,
        ...postPaging,
        ...override,
      };
      const result = await service.listPosts(payload);
      setPosts(Array.isArray(result?.items) ? result.items : []);
      setTotalPosts(Number(result?.total || 0));
      setPostPaging({
        page: Number(result?.page || payload.page || 1),
        page_size: Number(result?.page_size || payload.page_size || 20),
      });
      const availableIDs = new Set((Array.isArray(result?.items) ? result.items : []).map((x) => x?.id));
      setSelectedPostIDs((prev) => prev.filter((id) => availableIDs.has(id)));
    } catch (e) {
      message.error(e?.message || "Không thể tải danh sách blog");
    } finally {
      setLoading(false);
    }
  };

  const loadTaxonomies = async () => {
    try {
      const [cats, tagRows] = await Promise.all([service.listCategories({}), service.listTags({})]);
      setCategories(Array.isArray(cats) ? cats : []);
      setTags(Array.isArray(tagRows) ? tagRows : []);
    } catch (e) {
      message.error(e?.message || "Không thể tải danh mục/thẻ");
    }
  };

  const loadBrandProfile = async () => {
    setBrandLoading(true);
    try {
      const data = await service.getBrandProfile();
      brandForm.setFieldsValue({
        brand_name: data?.brand_name || "",
        slogan: data?.slogan || "",
        full_description: data?.full_description || "",
        value_proposition: data?.value_proposition || "",
        target_customer: data?.target_customer || "",
        products_and_strengths: data?.products_and_strengths || "",
        tone_of_voice: data?.tone_of_voice || "",
        ai_context_note: data?.ai_context_note || "",
        keywords: Array.isArray(data?.keywords) ? data.keywords : [],
      });
    } catch (e) {
      message.error(e?.message || "Không thể tải hồ sơ thương hiệu");
    } finally {
      setBrandLoading(false);
    }
  };

  useEffect(() => {
    loadPosts();
    loadTaxonomies();
    loadBrandProfile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const categoryOptions = useMemo(
    () => (categories || []).map((x) => ({ label: x.name, value: x.id })),
    [categories]
  );

  const tagOptions = useMemo(
    () => (tags || []).map((x) => ({ label: x.name, value: x.id })),
    [tags]
  );

  const setFormFromPost = (post) => {
    postForm.setFieldsValue({
      title: post.title || "",
      slug: post.slug || "",
      excerpt: post.excerpt || "",
      status: post.status || "draft",
      publish_at: toDatePickerValue(post.publish_at),
      unpublish_at: toDatePickerValue(post.unpublish_at),
      content_html: post.content_html || "",
      category_ids: Array.isArray(post.category_ids) ? post.category_ids : [],
      tag_ids: Array.isArray(post.tag_ids) ? post.tag_ids : [],
      featured_image: post.featured_image || null,
      gallery: Array.isArray(post.gallery) ? post.gallery : [],
      seo: {
        ...defaultSEO,
        ...(post.seo || {}),
      },
    });
  };

  const loadRevisions = async (post_id) => {
    if (!post_id) {
      setRevisions([]);
      return;
    }
    setRevisionLoading(true);
    try {
      const rows = await service.listRevisions({ post_id });
      setRevisions(Array.isArray(rows) ? rows : []);
    } catch (e) {
      message.error(e?.message || "Không thể tải các phiên bản");
    } finally {
      setRevisionLoading(false);
    }
  };

  const createPost = () => {
    const item = { ...defaultPost, seo: { ...defaultSEO } };
    setEditingPost(item);
    setFormFromPost(item);
    setRevisions([]);
    setAIFacebookContent("");
    setAIImagePrompt("");
    setAIGeneratedImage(null);
    setEditorOpen(true);
  };

  const editPost = async (record) => {
    setEditorLoading(true);
    try {
      const item = await service.getPost({ id: record.id });
      const normalized = {
        ...defaultPost,
        ...(item || {}),
        seo: { ...defaultSEO, ...(item?.seo || {}) },
      };
      setEditingPost(normalized);
      setFormFromPost(normalized);
      setAIFacebookContent(String(normalized?.facebook_post_content || ""));
      setAIImagePrompt("");
      setAIGeneratedImage(normalized?.facebook_image || null);
      setEditorOpen(true);
      await loadRevisions(normalized.id);
    } catch (e) {
      message.error(e?.message || "Không thể tải blog");
    } finally {
      setEditorLoading(false);
    }
  };

  const closeEditor = () => {
    if (editorLoading) return;
    setEditorOpen(false);
    setAIFacebookContent("");
    setAIImagePrompt("");
    setAIGeneratedImage(null);
  };

  const onEditorStatusChange = (status) => {
    if (status !== "scheduled") {
      postForm.setFieldValue("publish_at", null);
    }
  };

  const savePost = async () => {
    try {
      const values = await postForm.validateFields();
      setEditorLoading(true);

      const payload = {
        ...editingPost,
        ...values,
        facebook_post_content: String(aiFacebookContent || "").trim(),
        facebook_image: aiGeneratedImage || editingPost?.facebook_image || null,
        publish_at: toISO(values.publish_at),
        unpublish_at: toISO(values.unpublish_at),
        seo: {
          ...defaultSEO,
          ...(values.seo || {}),
          meta_keywords: Array.isArray(values?.seo?.meta_keywords)
            ? values.seo.meta_keywords
            : String(values?.seo?.meta_keywords || "")
                .split(",")
                .map((x) => x.trim())
                .filter(Boolean),
        },
      };

      if (payload.id) {
        await service.updatePost(payload);
        message.success("Cập nhật blog thành công");
      } else {
        await service.createPost(payload);
        message.success("Tạo blog thành công");
      }

      setEditorOpen(false);
      await loadPosts();
      await loadTaxonomies();
    } catch (e) {
      if (e?.errorFields) return;
      message.error(e?.message || "Lưu blog thất bại");
    } finally {
      setEditorLoading(false);
    }
  };

  const deletePost = (record) => {
    modal.confirm({
      title: "Xóa blog",
      content: `Bạn chắc chắn muốn xóa blog "${record?.title || ""}"?`,
      okText: "Xóa",
      okButtonProps: { danger: true },
      cancelText: "Hủy",
      onOk: async () => {
        try {
          await service.deletePost({ id: record.id });
          message.success("Đã xóa blog");
          await loadPosts();
        } catch (e) {
          message.error(e?.message || "Xóa blog thất bại");
        }
      },
    });
  };

  const restoreRevision = (revision) => {
    modal.confirm({
      title: "Khôi phục phiên bản",
      content: "Khôi phục phiên bản này sẽ ghi đè nội dung hiện tại.",
      okText: "Khôi phục",
      cancelText: "Hủy",
      onOk: async () => {
        try {
          const restored = await service.restoreRevision({ revision_id: revision.id });
          const normalized = {
            ...defaultPost,
            ...(restored || {}),
            seo: { ...defaultSEO, ...(restored?.seo || {}) },
          };
          setEditingPost(normalized);
          setFormFromPost(normalized);
          await loadRevisions(normalized.id);
          await loadPosts();
          message.success("Đã khôi phục phiên bản");
        } catch (e) {
          message.error(e?.message || "Khôi phục phiên bản thất bại");
        }
      },
    });
  };

  const saveCategory = async () => {
    if (!categoryName.trim()) {
      message.warning("Nhập tên danh mục");
      return;
    }
    setSavingCategory(true);
    try {
      await service.createCategory({ name: categoryName.trim() });
      setCategoryName("");
      await loadTaxonomies();
      message.success("Đã thêm danh mục");
    } catch (e) {
      message.error(e?.message || "Thêm danh mục thất bại");
    } finally {
      setSavingCategory(false);
    }
  };

  const removeCategory = async (record) => {
    try {
      await service.deleteCategory({ id: record.id });
      await loadTaxonomies();
      message.success("Đã xóa danh mục");
    } catch (e) {
      message.error(e?.message || "Xóa danh mục thất bại");
    }
  };

  const saveTag = async () => {
    if (!tagName.trim()) {
      message.warning("Nhập tên thẻ");
      return;
    }
    setSavingTag(true);
    try {
      await service.createTag({ name: tagName.trim() });
      setTagName("");
      await loadTaxonomies();
      message.success("Đã thêm thẻ");
    } catch (e) {
      message.error(e?.message || "Thêm thẻ thất bại");
    } finally {
      setSavingTag(false);
    }
  };

  const removeTag = async (record) => {
    try {
      await service.deleteTag({ id: record.id });
      await loadTaxonomies();
      message.success("Đã xóa thẻ");
    } catch (e) {
      message.error(e?.message || "Xóa thẻ thất bại");
    }
  };

  const onPostSearch = (nextFilters = {}) => {
    const merged = { ...postFilters, ...nextFilters };
    setPostFilters(merged);
    loadPosts({ ...merged, page: 1 });
  };

  const onPostPageChange = (page, page_size) => {
    const paging = { page, page_size };
    setPostPaging(paging);
    loadPosts({ ...paging });
  };

  const updateSelectedPostsStatus = async () => {
    const selectedIDs = Array.isArray(selectedPostIDs) ? selectedPostIDs : [];
    if (selectedIDs.length === 0) {
      message.warning("Vui lòng chọn ít nhất một bài viết");
      return;
    }

    const nextStatus = String(bulkPostStatus || "draft").trim();
    if (!nextStatus) {
      message.warning("Vui lòng chọn trạng thái cần cập nhật");
      return;
    }

    setBulkUpdatingStatus(true);
    try {
      const jobs = selectedIDs.map((id) => service.updatePost({ id, status: nextStatus }));
      const results = await Promise.allSettled(jobs);

      const successCount = results.filter((x) => x.status === "fulfilled").length;
      const failedCount = results.length - successCount;

      if (successCount > 0 && failedCount === 0) {
        message.success(`Đã cập nhật trạng thái ${successCount} bài viết`);
      } else if (successCount > 0) {
        message.warning(`Đã cập nhật ${successCount} bài viết, ${failedCount} bài bị lỗi`);
      } else {
        message.error("Không thể cập nhật trạng thái cho các bài đã chọn");
      }

      await loadPosts();
      setSelectedPostIDs([]);
    } catch (e) {
      message.error(e?.message || "Cập nhật trạng thái hàng loạt thất bại");
    } finally {
      setBulkUpdatingStatus(false);
    }
  };

  const checkSlug = async (value) => {
    if (!value) return;
    try {
      const result = await service.checkSlug({ slug: value, exclude_id: editingPost?.id || null });
      if (!result?.available && result?.slug) {
        message.info(`Slug "${result.slug}" đang được sử dụng hoặc thuộc route được bảo lưu`);
      }
    } catch (e) {
      message.error(e?.message || "Kiểm tra slug thất bại");
    }
  };

  const saveBrandProfile = async () => {
    try {
      const values = await brandForm.validateFields();
      setBrandSaving(true);
      await service.updateBrandProfile({
        ...values,
        keywords: Array.isArray(values.keywords) ? values.keywords : [],
      });
      message.success("Đã lưu hồ sơ thương hiệu");
      await loadBrandProfile();
    } catch (e) {
      if (e?.errorFields) return;
      message.error(e?.message || "Lưu hồ sơ thương hiệu thất bại");
    } finally {
      setBrandSaving(false);
    }
  };

  const openWebsite = () => {
    const url = getPublicWebsiteBaseURL();
    if (!url) {
      message.warning("Không xác định được địa chỉ website để xem");
      return;
    }
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const openPostPreview = (post, useFormSlug = false) => {
    const formSlug = postForm.getFieldValue("slug");
    const slug = String(useFormSlug ? formSlug || post?.slug || "" : post?.slug || formSlug || "").trim();
    if (!slug) {
      message.warning("Vui lòng nhập slug để xem blog");
      return;
    }

    const baseURL = getPublicWebsiteBaseURL();
    if (!baseURL) {
      message.warning("Không xác định được địa chỉ website để xem blog");
      return;
    }

    const previewURL = `${baseURL.replace(/\/$/, "")}/${encodeURIComponent(slug)}`;
    window.open(previewURL, "_blank", "noopener,noreferrer");
  };

  const openSitemap = () => {
    const sitemapURL = getPublicSitemapURL();
    if (!sitemapURL) {
      message.warning("Không xác định được link sitemap");
      return;
    }
    window.open(sitemapURL, "_blank", "noopener,noreferrer");
  };

  const copySitemapLink = async () => {
    const sitemapURL = getPublicSitemapURL();
    if (!sitemapURL) {
      message.warning("Không xác định được link sitemap");
      return;
    }

    try {
      await navigator.clipboard.writeText(sitemapURL);
      message.success("Đã copy link sitemap");
    } catch (e) {
      message.error(e?.message || "Copy link sitemap thất bại");
    }
  };

  const downloadSitemap = async () => {
    const sitemapURL = getPublicSitemapURL();
    if (!sitemapURL) {
      message.warning("Không xác định được link sitemap");
      return;
    }

    try {
      const response = await fetch(sitemapURL, { method: "GET" });
      if (!response.ok) {
        throw new Error("Không thể tải file sitemap");
      }

      const blob = await response.blob();
      const objectURL = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = objectURL;
      link.download = "sitemap.xml";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(objectURL);
      message.success("Đã tải sitemap");
    } catch (e) {
      message.error(e?.message || "Tải sitemap thất bại");
    }
  };

  const pingGoogleSitemap = async () => {
    const sitemapURL = getPrimarySitemapURL() || getPublicSitemapURL();
    if (!sitemapURL) {
      message.warning("Không xác định được link sitemap để ping Google");
      return;
    }

    try {
      setPingingGoogleSitemap(true);
      await service.pingGoogleSitemap({ sitemap_url: sitemapURL });
      message.success("Đã gửi yêu cầu ping Google để cập nhật sitemap");
    } catch (e) {
      message.error(e?.message || "Ping Google sitemap thất bại");
    } finally {
      setPingingGoogleSitemap(false);
    }
  };

  const generatePostHTMLByAI = async () => {
    try {
      const title = String(postForm.getFieldValue("title") || "").trim();
      const excerpt = String(postForm.getFieldValue("excerpt") || "").trim();
      const seoForm = postForm.getFieldValue("seo") || {};
      const seo = {
        meta_title: String(seoForm?.meta_title || "").trim(),
        meta_description: String(seoForm?.meta_description || "").trim(),
        meta_keywords: Array.isArray(seoForm?.meta_keywords)
          ? seoForm.meta_keywords
          : String(seoForm?.meta_keywords || "")
              .split(",")
              .map((x) => x.trim())
              .filter(Boolean),
      };

      if (!title) {
        message.warning("Vui lòng nhập tiêu đề trước khi tạo nội dung bằng AI");
        return;
      }

      setAIGenerating(true);
      const result = await service.generateHTMLByAI({ title, excerpt, seo });
      const html = String(result?.content_html || "").trim();

      if (!html) {
        message.error("AI chưa tạo được nội dung HTML hợp lệ");
        return;
      }

      postForm.setFieldValue("content_html", html);
      message.success("Đã tạo nội dung HTML bằng AI");
    } catch (e) {
      message.error(e?.message || "Tạo nội dung bằng AI thất bại");
    } finally {
      setAIGenerating(false);
    }
  };

  const generateFacebookPostByAI = async () => {
    try {
      const title = String(postForm.getFieldValue("title") || "").trim();
      const excerpt = String(postForm.getFieldValue("excerpt") || "").trim();
      const content_html = String(postForm.getFieldValue("content_html") || "").trim();
      const seoForm = postForm.getFieldValue("seo") || {};
      const seo = {
        meta_title: String(seoForm?.meta_title || "").trim(),
        meta_description: String(seoForm?.meta_description || "").trim(),
        meta_keywords: Array.isArray(seoForm?.meta_keywords)
          ? seoForm.meta_keywords
          : String(seoForm?.meta_keywords || "")
              .split(",")
              .map((x) => x.trim())
              .filter(Boolean),
      };

      if (!title && !excerpt && !content_html) {
        message.warning("Vui lòng nhập tiêu đề, tóm tắt hoặc nội dung bài trước khi tạo caption Facebook");
        return;
      }

      setAIFacebookGenerating(true);
      const result = await service.generateFacebookPostByAI({ title, excerpt, content_html, seo });
      const content = String(result?.content || "").trim();

      if (!content) {
        message.error("AI chưa tạo được caption Facebook hợp lệ");
        return;
      }

      setAIFacebookContent(content);
      setEditingPost((prev) => ({ ...(prev || {}), facebook_post_content: content }));
      message.success("Đã tạo caption Facebook bằng AI");
    } catch (e) {
      message.error(e?.message || "Tạo caption Facebook bằng AI thất bại");
    } finally {
      setAIFacebookGenerating(false);
    }
  };

  const copyFacebookPostContent = async () => {
    const content = String(aiFacebookContent || "").trim();
    if (!content) {
      message.warning("Chưa có caption Facebook để copy");
      return;
    }

    try {
      await navigator.clipboard.writeText(content);
      message.success("Đã copy caption Facebook");
    } catch (e) {
      message.error(e?.message || "Copy caption Facebook thất bại");
    }
  };

  const applyGeneratedImageAsFeatured = () => {
    if (!aiGeneratedImage?.url) {
      message.warning("Chưa có ảnh AI để gán ảnh đại diện");
      return;
    }
    postForm.setFieldValue("featured_image", aiGeneratedImage);
    message.success("Đã gán ảnh AI làm ảnh đại diện");
  };

  const appendGeneratedImageToGallery = () => {
    if (!aiGeneratedImage?.url) {
      message.warning("Chưa có ảnh AI để thêm vào gallery");
      return;
    }
    const currentGallery = Array.isArray(postForm.getFieldValue("gallery")) ? postForm.getFieldValue("gallery") : [];
    const exists = currentGallery.some((x) => x?.id === aiGeneratedImage?.id || x?.url === aiGeneratedImage?.url);
    if (exists) {
      message.info("Ảnh này đã có trong gallery");
      return;
    }
    postForm.setFieldValue("gallery", [aiGeneratedImage, ...currentGallery]);
    message.success("Đã thêm ảnh AI vào gallery");
  };

  const generatePostImageByAI = async () => {
    try {
      const title = String(postForm.getFieldValue("title") || "").trim();
      const excerpt = String(postForm.getFieldValue("excerpt") || "").trim();
      const prompt = String(aiImagePrompt || "").trim();

      if (!title && !prompt) {
        message.warning("Vui lòng nhập tiêu đề hoặc prompt để tạo hình ảnh");
        return;
      }

      setAIImageGenerating(true);
      const image = await service.generateImageByAI({ title, excerpt, prompt });

      if (!image?.url) {
        message.error("AI chưa tạo được hình ảnh hợp lệ");
        return;
      }

      setAIGeneratedImage(image);
      setEditingPost((prev) => ({ ...(prev || {}), facebook_image: image }));
      message.success("Đã tạo hình ảnh bằng AI");
    } catch (e) {
      message.error(e?.message || "Tạo hình ảnh bằng AI thất bại");
    } finally {
      setAIImageGenerating(false);
    }
  };

  const openBulkAIDrawer = () => {
    setBulkAIOpen(true);
  };

  const closeBulkAIDrawer = () => {
    if (bulkAIGeneratingTopics || bulkAICreatingPosts) {
      return;
    }
    setBulkAIOpen(false);
  };

  const generateTopicListByAI = async () => {
    try {
      const topic = String(bulkAITopic || "").trim();
      const count = Math.max(1, Math.min(30, Number(bulkAICount) || 5));

      if (!topic) {
        message.warning("Vui lòng nhập chủ đề trước khi tạo danh sách bài viết");
        return;
      }

      setBulkAIGeneratingTopics(true);
      const response = await service.generateTopicsByAI({ topic, count });
      const items = Array.isArray(response?.items)
        ? response.items.map((item, index) => ({
            ...item,
            id: item?.id || `topic-${index + 1}`,
          }))
        : [];

      if (items.length === 0) {
        message.warning("AI chưa tạo được danh sách chủ đề phù hợp");
        return;
      }

      setBulkAITopics(items);
      setBulkAISelectedIDs(items.map((x) => x.id));
      message.success(`Đã tạo ${items.length} chủ đề bằng AI`);
    } catch (e) {
      message.error(e?.message || "Tạo danh sách chủ đề bằng AI thất bại");
    } finally {
      setBulkAIGeneratingTopics(false);
    }
  };

  const createSelectedPostsByAI = async () => {
    try {
      const selectedTopics = bulkAITopics.filter((item) => bulkAISelectedIDs.includes(item.id));

      if (selectedTopics.length === 0) {
        message.warning("Vui lòng chọn ít nhất một chủ đề để tạo bài viết");
        return;
      }

      setBulkAICreatingPosts(true);
      const result = await service.bulkCreatePostsByAI({
        topics: selectedTopics,
        status: "draft",
      });

      const summary = result?.summary || {};
      const created = Number(summary?.created || 0);
      const failed = Number(summary?.failed || 0);

      if (created > 0 && failed === 0) {
        message.success(`Đã tạo ${created} bài viết nháp bằng AI`);
      } else if (created > 0 && failed > 0) {
        message.warning(`Đã tạo ${created} bài viết, ${failed} bài bị lỗi. Vui lòng kiểm tra lại danh sách.`);
      } else {
        message.error("Không tạo được bài viết nào từ danh sách đã chọn");
      }

      await loadPosts();
      setBulkAIOpen(false);
      setBulkAITopics([]);
      setBulkAISelectedIDs([]);
    } catch (e) {
      message.error(e?.message || "Tạo bài viết hàng loạt bằng AI thất bại");
    } finally {
      setBulkAICreatingPosts(false);
    }
  };

  return {
    loading,
    posts,
    totalPosts,
    postPaging,
    postFilters,
    selectedPostIDs,
    setSelectedPostIDs,
    bulkPostStatus,
    setBulkPostStatus,
    bulkUpdatingStatus,
    categories,
    tags,
    categoryOptions,
    tagOptions,
    editorOpen,
    editingPost,
    editorLoading,
    revisions,
    revisionLoading,
    aiGenerating,
    aiFacebookGenerating,
    aiFacebookContent,
    setAIFacebookContent,
    aiImageGenerating,
    aiImagePrompt,
    setAIImagePrompt,
    aiGeneratedImage,
    setAIGeneratedImage,
    bulkAIOpen,
    bulkAITopic,
    setBulkAITopic,
    bulkAICount,
    setBulkAICount,
    bulkAITopics,
    bulkAISelectedIDs,
    setBulkAISelectedIDs,
    bulkAIGeneratingTopics,
    bulkAICreatingPosts,
    pingingGoogleSitemap,
    postForm,
    createPost,
    editPost,
    closeEditor,
    savePost,
    deletePost,
    restoreRevision,
    onEditorStatusChange,
    onPostSearch,
    onPostPageChange,
    updateSelectedPostsStatus,
    checkSlug,
    categoryName,
    setCategoryName,
    saveCategory,
    removeCategory,
    savingCategory,
    tagName,
    setTagName,
    saveTag,
    removeTag,
    savingTag,
    brandForm,
    brandLoading,
    brandSaving,
    saveBrandProfile,
    openWebsite,
    openPostPreview,
    generatePostHTMLByAI,
    generateFacebookPostByAI,
    copyFacebookPostContent,
    generatePostImageByAI,
    applyGeneratedImageAsFeatured,
    appendGeneratedImageToGallery,
    openBulkAIDrawer,
    closeBulkAIDrawer,
    generateTopicListByAI,
    createSelectedPostsByAI,
    openSitemap,
    copySitemapLink,
    downloadSitemap,
    pingGoogleSitemap,
    reload: async () => {
      await loadPosts();
      await loadTaxonomies();
      await loadBrandProfile();
    },
  };
};

export default useComponent;
